import { useState, useEffect, useRef } from 'react';

const slides = [
  {
    id: 2,
    category: '쇼호스트',
    title: '라이브로 만나는\n특별한 쇼핑 경험',
    subtitle: '라이브커머스 전문 쇼호스트 어정희',
    cta: '방송 보러가기',
    targetSection: 'live',
    icon: 'ri-live-line',
    cardImage: 'https://static.readdy.ai/image/3c653b3a8a919d24e4f7a2804547f79c/a14a4011ce869156bf759ca8f7604aa3.png',
    cardLabel: '라이브커머스 방송',
    tagColor: 'bg-emerald-500',
  },
  {
    id: 1,
    category: '교회사 연구자',
    title: '역사 속 믿음의\n발자취를 연구합니다',
    subtitle: '교회사 연구자 어정희 | 사람아이앤지',
    cta: '연구 활동 보기',
    targetSection: 'church',
    icon: 'ri-book-open-line',
    cardImage: 'https://static.readdy.ai/image/3c653b3a8a919d24e4f7a2804547f79c/040822afed5f032758dd29741ee2d2b8.png',
    cardLabel: '교회사 연구 활동',
    tagColor: 'bg-amber-500',
  },
  {
    id: 3,
    category: 'AI 강사',
    title: 'AI 시대, 누구나\n쉽게 시작할 수 있습니다',
    subtitle: 'AI 활용 교육 전문 강사 어정희',
    cta: '강의 알아보기',
    targetSection: 'ai',
    icon: 'ri-robot-line',
    cardImage: 'https://static.readdy.ai/image/3c653b3a8a919d24e4f7a2804547f79c/cb53cc2884365f3d28999eacedf86f47.png',
    cardLabel: 'AI 활용 교육',
    tagColor: 'bg-violet-500',
  },
  {
    id: 4,
    category: '영화와 인문학',
    title: '영화로 읽는\n삶과 철학의 이야기',
    subtitle: '인문학 강사 어정희 | 영화와 인문학',
    cta: '강좌 알아보기',
    targetSection: 'film',
    icon: 'ri-film-line',
    cardImage: 'https://static.readdy.ai/image/3c653b3a8a919d24e4f7a2804547f79c/5e2608328e2c9afd3268fdb501d4ecf5.jpeg',
    cardLabel: '영화와 인문학 강좌',
    tagColor: 'bg-rose-500',
  },
];

const navLinks = [
  { label: '소개', href: '#about' },
  { label: '교회사연구', href: '#church' },
  { label: '라이브커머스', href: '#live' },
  { label: 'AI교육', href: '#ai' },
  { label: '영화와 인문학', href: '#film' },
  { label: '문의', href: '#contact' },
];

export default function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [nextSlideIndex, setNextSlideIndex] = useState<number | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const autoPlayRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const changeSlide = (nextIndex: number) => {
    if (isAnimating || nextIndex === currentSlide) return;
    setIsAnimating(true);
    setNextSlideIndex(nextIndex);
    setTimeout(() => {
      setCurrentSlide(nextIndex);
      setNextSlideIndex(null);
      setIsAnimating(false);
    }, 700);
  };

  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      const next = (currentSlide + 1) % slides.length;
      changeSlide(next);
    }, 4000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, currentSlide, isAnimating]);

  const pauseAndResume = () => {
    setIsAutoPlaying(false);
    if (autoPlayRef.current) clearTimeout(autoPlayRef.current);
    autoPlayRef.current = setTimeout(() => setIsAutoPlaying(true), 8000);
  };

  const goToSlide = (index: number) => {
    if (index === currentSlide || isAnimating) return;
    changeSlide(index);
    pauseAndResume();
  };

  const handleNextSlide = () => {
    const next = (currentSlide + 1) % slides.length;
    changeSlide(next);
    pauseAndResume();
  };

  const handlePrevSlide = () => {
    const prev = (currentSlide - 1 + slides.length) % slides.length;
    changeSlide(prev);
    pauseAndResume();
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  const handleNavClick = (href: string) => {
    const id = href.replace('#', '');
    scrollToSection(id);
    setMobileMenuOpen(false);
  };

  const slide = slides[currentSlide];
  const nextSlide = nextSlideIndex !== null ? slides[nextSlideIndex] : null;

  return (
    <>
      {/* 상단 네비게이션 */}
      <nav className="fixed top-8 left-0 right-0 z-50 flex justify-center px-3">
        <div className="flex items-center justify-between bg-white rounded-full shadow-lg px-10 md:px-14 py-4 w-full max-w-[1800px]">
          {/* 로고 */}
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="flex items-center gap-1 cursor-pointer"
          >
            <img
              src="https://static.readdy.ai/image/3c653b3a8a919d24e4f7a2804547f79c/c97ed4e2b32ef72e5f212ef77b114289.png"
              alt="EUH JUNG HEE 로고"
              className="h-14 md:h-18 w-auto object-contain"
              style={{ minHeight: '56px', maxHeight: '72px' }}
            />
          </a>

          {/* 데스크탑 네비게이션 메뉴 */}
          <div className="hidden md:flex items-center gap-2">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => handleNavClick(link.href)}
                className="px-7 py-3 rounded-full text-lg font-bold text-gray-800 hover:bg-gray-100 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                {link.label}
              </button>
            ))}
            <button
              onClick={() => handleNavClick('#contact')}
              className="ml-3 px-8 py-3 rounded-full text-lg font-extrabold bg-gray-900 text-white hover:bg-gray-700 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              연락하기
            </button>
          </div>

          {/* 모바일 햄버거 버튼 */}
          <button
            className="md:hidden w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-all cursor-pointer"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="메뉴 열기"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-gray-900 text-2xl`}></i>
          </button>
        </div>

        {/* 모바일 드롭다운 메뉴 */}
        {mobileMenuOpen && (
          <div className="absolute top-24 left-3 right-3 bg-white rounded-2xl shadow-xl py-3 px-2 flex flex-col gap-1">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => handleNavClick(link.href)}
                className="w-full text-left px-4 py-3 rounded-xl text-lg font-bold text-gray-800 hover:bg-gray-100 transition-all cursor-pointer"
              >
                {link.label}
              </button>
            ))}
            <button
              onClick={() => handleNavClick('#contact')}
              className="w-full mt-1 px-4 py-3 rounded-xl text-lg font-extrabold bg-gray-900 text-white hover:bg-gray-700 transition-all cursor-pointer"
            >
              연락하기
            </button>
          </div>
        )}
      </nav>

      {/* 히어로 섹션 */}
      <section
        className="relative w-full min-h-screen flex flex-col md:flex-row items-stretch"
        style={{ backgroundColor: '#DFFF00' }}
      >
        {/* 왼쪽(모바일: 상단): 고정 소개 영역 */}
        <div className="relative z-10 flex flex-col justify-center px-6 md:px-16 py-8 md:py-20 w-full md:w-1/2 pt-32 md:pt-40">
          <div className="relative z-10 max-w-xl mx-auto md:mx-0">

            {/* 메인 카피 */}
            <div className="flex flex-col gap-4">
              {/* 두 줄 - 설립체 유건욱 */}
              <div>
                <p
                  className="leading-[1.2]"
                  style={{
                    fontFamily: "'establishRetrosansOTF', 'Black Han Sans', sans-serif",
                    fontWeight: 900,
                    color: '#1a4d1a',
                    wordBreak: 'keep-all',
                    fontSize: 'clamp(1.9rem, 3.8vw, 3.6rem)',
                    letterSpacing: '-0.01em',
                  }}
                >
                  마음을 다하는 진정성<br />
                  다름을 만드는 스토리
                </p>
              </div>

              {/* 구분선 */}
              <div style={{ width: '48px', height: '3px', backgroundColor: '#1a4d1a', borderRadius: '2px', margin: '4px 0' }} />

              {/* 아래 문장 - 일반 고딕 */}
              <p
                className="leading-[1.8]"
                style={{
                  fontFamily: "'Noto Sans KR', 'Apple SD Gothic Neo', sans-serif",
                  fontWeight: 400,
                  color: '#1a4d1a',
                  wordBreak: 'keep-all',
                  fontSize: 'clamp(0.95rem, 1.6vw, 1.1rem)',
                }}
              >
                라이브커머스 카메라 앞에서도, AI 교육 현장에서도,<br />
                오랜 역사를 탐구할 때도 가장 중요한 것은 &apos;사람&apos;입니다.<br />
                숫자보다 가치를 먼저 생각하는 크리에이터{' '}
                <span style={{ fontWeight: 700, color: '#0f3d0f' }}>어정희</span>
                입니다.
              </p>
            </div>

            {/* 슬라이드 인디케이터 */}
            <div className="flex items-center gap-3 md:gap-4 mt-12 md:mt-16">
              <button
                onClick={handlePrevSlide}
                className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full border-2 border-gray-900/30 bg-black/10 hover:bg-black/20 transition-all duration-200 cursor-pointer"
                aria-label="이전 슬라이드"
              >
                <i className="ri-arrow-up-s-line text-gray-900 text-xl"></i>
              </button>
              <div className="flex flex-col gap-2">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`rounded-full transition-all duration-300 cursor-pointer ${
                      index === currentSlide
                        ? 'h-8 w-2.5 bg-gray-900'
                        : 'h-2.5 w-2.5 bg-gray-900/30 hover:bg-gray-900/50'
                    }`}
                    aria-label={`슬라이드 ${index + 1}`}
                  />
                ))}
              </div>
              <button
                onClick={handleNextSlide}
                className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full border-2 border-gray-900/30 bg-black/10 hover:bg-black/20 transition-all duration-200 cursor-pointer"
                aria-label="다음 슬라이드"
              >
                <i className="ri-arrow-down-s-line text-gray-900 text-xl"></i>
              </button>
              <span className="text-sm text-gray-700 ml-1 md:ml-2 font-semibold">
                <span className="text-gray-900 font-bold text-base">
                  {String(currentSlide + 1).padStart(2, '0')}
                </span>
                {' / '}
                {String(slides.length).padStart(2, '0')}
              </span>
            </div>
          </div>
        </div>

        {/* 오른쪽(모바일: 하단): 카드 롤링 */}
        <div className="relative w-full md:w-1/2 flex items-center justify-center md:py-0 px-4 md:px-8 pb-10 md:pb-0">
          <div
            className="relative w-full"
            style={{
              aspectRatio: '1/1',
              maxWidth: 'min(88vw, calc(50vw - 48px))',
              minWidth: '320px',
            }}
          >
            {/* 현재 슬라이드 카드 */}
            <div
              className="absolute inset-0"
              style={{
                animation: isAnimating
                  ? 'cardSlideOut 0.7s cubic-bezier(0.4, 0, 0.2, 1) forwards'
                  : 'none',
                zIndex: 2,
              }}
            >
              <SlideCard slide={slide} scrollToSection={scrollToSection} />
            </div>

            {/* 다음 슬라이드 카드 */}
            {nextSlide && (
              <div
                className="absolute inset-0"
                style={{
                  animation: 'cardSlideIn 0.7s cubic-bezier(0.4, 0, 0.2, 1) forwards',
                  zIndex: 1,
                }}
              >
                <SlideCard slide={nextSlide} scrollToSection={scrollToSection} />
              </div>
            )}
          </div>
        </div>

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=EstablishRetrosansOTF:wght@900&display=swap');
          @keyframes cardSlideOut {
            from { transform: translateY(0); opacity: 1; }
            to   { transform: translateY(-120%); opacity: 0.6; }
          }
          @keyframes cardSlideIn {
            from { transform: translateY(100%); opacity: 0.6; }
            to   { transform: translateY(0); opacity: 1; }
          }
        `}</style>
      </section>
    </>
  );
}

interface SlideData {
  id: number;
  category: string;
  title: string;
  subtitle: string;
  cta: string;
  targetSection: string;
  icon: string;
  cardImage: string;
  cardLabel: string;
  tagColor: string;
}

function SlideCard({ slide, scrollToSection }: { slide: SlideData; scrollToSection: (id: string) => void }) {
  return (
    <div
      className="relative w-full h-full rounded-3xl overflow-hidden shadow-2xl"
    >
      <img
        src={slide.cardImage}
        alt={`어정희 ${slide.category} - ${slide.cardLabel}`}
        className="w-full h-full object-cover object-top"
      />
      <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 bg-gradient-to-t from-black/75 via-black/30 to-transparent">
        <div className="flex items-end justify-between gap-2">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className={`w-2.5 h-2.5 rounded-full ${slide.tagColor}`}></span>
              <span className="text-white text-sm md:text-base font-bold">{slide.cardLabel}</span>
            </div>
            <p className="text-white/80 text-xs md:text-sm">{slide.subtitle}</p>
          </div>
          <button
            onClick={() => scrollToSection(slide.targetSection)}
            className="px-3 md:px-5 py-2 md:py-2.5 bg-white text-gray-900 rounded-full text-xs md:text-sm font-bold hover:bg-gray-100 transition-all duration-200 cursor-pointer whitespace-nowrap shadow-md flex-shrink-0"
          >
            {slide.cta} →
          </button>
        </div>
      </div>
      <div className="absolute top-4 md:top-5 left-4 md:left-5">
        <span
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 md:px-3.5 md:py-2 rounded-full text-white text-xs md:text-sm font-bold ${slide.tagColor} shadow-lg`}
        >
          <i className={`${slide.icon} text-sm md:text-base`}></i>
          {slide.category}
        </span>
      </div>
    </div>
  );
}
